import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import { resolvePlanId, PRICING_TIERS } from "../../../constants/pricingScreen";
import { shouldHideOffPlatformAppBilling } from "../../../utils";
import {
  dunningPastDueUntil,
  dunningUnpaidTitle,
} from "../../../helpers/dunningCopy";

const planName = (planId) => {
  const canonical = resolvePlanId(planId);
  if (canonical === "enterprise") return "Enterprise";
  const tier = PRICING_TIERS.find((t) => t.planId === canonical);
  return tier?.name || "your paid";
};

const daysLeftFrom = (grace) => {
  // grace may be a Firestore-serialized timestamp ({_seconds}) or epoch seconds.
  const epoch =
    typeof grace === "number"
      ? grace
      : grace?._seconds ?? grace?.seconds ?? null;
  if (!epoch) return null;
  return Math.max(0, moment.unix(epoch).diff(moment(), "days"));
};

const money = (cents, currency = "usd") =>
  cents == null
    ? null
    : `$${(cents / 100).toFixed(2)} ${String(currency).toUpperCase()}`;

/**
 * Global, explicit billing past-due / suspended banner. Reads the dunning state
 * the subscription webhook writes onto enterprises/{id}.dunning (delivered via
 * /current_user). Only shown to enterprise admins while a dunning state exists.
 */
const DunningBanner = () => {
  const navigate = useNavigate();
  const dunning = useSelector(
    (state) => state?.currentUser?.enterprise?.dunning
  );
  const isEnterpriseAdmin = useSelector(
    (state) => state?.currentUser?.user?.isEnterpriseAdmin
  );
  const enterprise = useSelector((state) => state?.currentUser?.enterprise);
  const enterprisePlanId = enterprise?.planId;

  if (shouldHideOffPlatformAppBilling(enterprise)) return null;
  if (!dunning || !isEnterpriseAdmin) return null;
  const status = dunning.status;
  if (status !== "past_due" && status !== "unpaid") return null;

  const downgraded = !!dunning.downgraded || status === "unpaid";
  const days = daysLeftFrom(dunning.gracePeriodEndsAt);
  const amount = money(dunning.amountDueCents, dunning.currency);
  const planId = dunning.previousPlanId || enterprisePlanId;
  const plan = planName(planId);
  const payUrl = dunning.hostedInvoiceUrl;

  const goToBilling = () => navigate("/settings/billing");

  const actions = (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        startIcon={<CreditCardIcon />}
        onClick={goToBilling}
        sx={{ color: "#111", bgcolor: "#fff", "&:hover": { bgcolor: "#f1f1f1" } }}
      >
        Update payment method
      </Button>
      {amount && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={goToBilling}
          sx={{ color: "#fff", borderColor: "#fff" }}
        >
          {`Pay ${amount} now`}
        </Button>
      )}
    </Stack>
  );

  if (downgraded) {
    return (
      <Alert
        severity="error"
        variant="filled"
        sx={{ borderRadius: 0, alignItems: "flex-start" }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          {dunningUnpaidTitle(planId)}
        </AlertTitle>
        We couldn’t collect your subscription payment{amount ? ` of ${amount}` : ""},
        so your <b>{plan}</b> plan features are paused. Your data and storefronts
        are still live. Add a working payment method to restore your plan
        immediately.
        {actions}
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      variant="filled"
      sx={{ borderRadius: 0, alignItems: "flex-start" }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        Your subscription payment failed
      </AlertTitle>
      We couldn’t charge your card{amount ? ` ${amount}` : ""} for your{" "}
      <b>{plan}</b> plan.{" "}
      {days != null ? (
        <>
          You have <b>{days} day{days === 1 ? "" : "s"}</b> to update your payment
          method {dunningPastDueUntil(planId)}.
        </>
      ) : (
        <>Please update your payment method to avoid losing your plan features.</>
      )}
      {actions}
    </Alert>
  );
};

export default DunningBanner;
